import QuickInformationDisplay from "@components/QuickInformationDisplay/QuickInformationDisplay"
import JIDRInputCard from "@components/JIDRInputCard/JIDRInputCard"
import RedemptionTargetSelectPopUp from "@components/RedemptionTargetSelectPopUp/RedemptionTargetSelectPopUp"
import SectionLabel from "@components/SectionLabel/SectionLabel"
import TextInput from "@components/TextInput/TextInput"
import { MINIMUM_BANK_REDEMPTION } from "@constants/transaction"
import { useNumericKeyboard } from "@hooks/useNumericKeyboard"
import { toCurrencyFormat } from "@lib/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import useFavoriteEWallet from "@hooks/useFavoriteEWallet"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import { useTranslation } from "react-i18next"
import {
    ScrollContainer,
    RedemptionDetailsContainer,
    EWalletInfoContainer,
    ButtonContainer,
    InfoContainer
} from "./RedeemEWalletContent.styles"
import { RedemptionOption } from "../../types/redemption"
import { SupportedEWallet } from "@hooks/useSupportedEWallet"

const SCROLL_OFFSET_MEMO = 200;
const FOCUS_SCROLL_DELAY = 500;

const RedeemEWalletAdditionalInformation = ({
    currentBalance,
    minimumRedemption,
    remainingLimit,
    totalDailyLimit,
    memo,
    onChangeMemo,
    onMemoFocus
}: {
    currentBalance: number | 'unavailable'
    minimumRedemption: number
    remainingLimit: number
    totalDailyLimit: number
    memo: string
    onChangeMemo: (value: React.ChangeEvent<HTMLInputElement>) => void
    onMemoFocus: () => void
}) => {
    const { t } = useTranslation()
    const memoInputRef = useRef<HTMLInputElement>(null);
    return (
        <div>
            <InfoContainer>
                <QuickInformationDisplay
                    fieldName={t('redeem.common.currentBalance')}
                    value={typeof currentBalance === 'number' ? `${toCurrencyFormat(currentBalance)} JIDR` : t('redeem.common.unavailable')}
                />
                <QuickInformationDisplay
                    fieldName={t('redeem.common.minimumRedemption')}
                    value={`${toCurrencyFormat(minimumRedemption)} JIDR`}
                />
                <QuickInformationDisplay
                    fieldName={t('redeem.eWallet.transactionLimit')}
                    value={`${toCurrencyFormat(Math.min(totalDailyLimit, remainingLimit))} JIDR`}
                    boldValue
                />
            </InfoContainer>
            <TextInput
                value={memo}
                onChange={onChangeMemo}
                title={t('redeem.common.memo')}
                isOptional
                onFocus={onMemoFocus}
                placeholder={t('redeem.common.memoPlaceholder')}
                ref={memoInputRef}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        memoInputRef.current?.blur();
                    }
                }}
            />
        </div>
    )
}

const RedeemEWalletContent = ({
    currentBalance,
    remainingLimit,
    totalDailyLimit,
    preselectedEWallet,
    prefilledAmount,
    redemptionOptions,
    supportedEWallets,
    onRemoveEWallet,
}: {
    currentBalance: number | 'unavailable'
    remainingLimit: number
    totalDailyLimit: number
    preselectedEWallet?: RedemptionOption
    prefilledAmount?: number
    redemptionOptions: RedemptionOption[]
    supportedEWallets?: SupportedEWallet[]
    onRemoveEWallet?: (ewallet_uuid: string) => Promise<void>
}) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null);
    const { isFavorite, toggleFavorite } = useFavoriteEWallet()
    const [eWalletMemo, setEWalletMemo] = useState('')
    const [selectedEWallet, setSelectedEWallet] = useState<RedemptionOption | undefined>(preselectedEWallet)
    const { amount, handleKeyboardPress, setAmount } = useNumericKeyboard(prefilledAmount || 0)

    const [amountError, setAmountError] = useState({
        isError: false,
        message: ''
    })

    const validateAmount = useCallback((amountToValidate?: number) => {
        const valueToValidate = amountToValidate ?? amount
        if (valueToValidate < MINIMUM_BANK_REDEMPTION) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.minimumAmount', { amount: toCurrencyFormat(MINIMUM_BANK_REDEMPTION) })
            })
        } else if (typeof currentBalance !== 'number' || valueToValidate > currentBalance) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.insufficientBalance')
            })
        } else if (valueToValidate > totalDailyLimit) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.maximumAmount', { amount: toCurrencyFormat(totalDailyLimit) })
            })
        } else if (valueToValidate > remainingLimit) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.maximumAmount', { amount: toCurrencyFormat(remainingLimit) })
            })
        } else {
            setAmountError({
                isError: false,
                message: ''
            })
        }
    }, [amount, currentBalance, totalDailyLimit, remainingLimit, t])

    useEffect(() => {
        if (prefilledAmount && prefilledAmount > 0) {
            validateAmount()
        }
    }, [prefilledAmount, validateAmount])

    const handleFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_MEMO;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY);
    }

    return (
        <ScrollContainer ref={containerRef}>
            <RedemptionDetailsContainer>
                <SectionLabel>{t('redeem.common.redemptionDetails')}</SectionLabel>
                <JIDRInputCard
                    handleKeyboardPress={handleKeyboardPress}
                    title={t('redeem.common.inputAmount')}
                    amount={amount}
                    isAmountError={amountError.isError}
                    errorText={amountError.message}
                    onBlur={() => validateAmount()}
                    onMax={typeof currentBalance === 'number' ? () => {
                        const maxAmount = Math.min(currentBalance, remainingLimit, totalDailyLimit)
                        setAmount(maxAmount)
                        setTimeout(() => validateAmount(maxAmount), 0)
                    } : undefined}
                    children={
                        <RedeemEWalletAdditionalInformation
                            currentBalance={currentBalance}
                            minimumRedemption={MINIMUM_BANK_REDEMPTION}
                            remainingLimit={remainingLimit}
                            totalDailyLimit={supportedEWallets?.find(ewallet => ewallet.provider === selectedEWallet?.provider)?.max_amount || totalDailyLimit}
                            memo={eWalletMemo}
                            onChangeMemo={(e) => setEWalletMemo(e.target.value)}
                            onMemoFocus={handleFocus}
                        />
                    }
                />
            </RedemptionDetailsContainer>
            <EWalletInfoContainer>
                <SectionLabel>{t('redeem.eWallet.eWalletInformation')}</SectionLabel>
                <RedemptionTargetSelectPopUp
                    type="e-wallet"
                    oneLine
                    options={[...redemptionOptions].sort(
                        (a, b) => (isFavorite(b.id) ? 1 : 0) - (isFavorite(a.id) ? 1 : 0)
                    )}
                    preselect={selectedEWallet}
                    onSelect={setSelectedEWallet}
                    currentAmount={amount}
                    onRemove={onRemoveEWallet}
                    isFavorite={isFavorite}
                    onFavoriteToggle={toggleFavorite}
                />
            </EWalletInfoContainer>
            <ButtonContainer>
                <PrimaryButton
                    onClick={() => navigate('/redeem-confirmation/e-wallet', {
                        state: {
                            redeemInformation: JSON.stringify({
                                redemptionTarget: selectedEWallet,
                                amount,
                                memo: eWalletMemo,
                            })
                        }
                    })}
                    disabled={
                        !selectedEWallet ||
                        amount <= 0 ||
                        amountError.isError
                    }
                >
                    {t('redeem.common.continueButton')}
                </PrimaryButton>
            </ButtonContainer>
        </ScrollContainer>
    )
}

export default RedeemEWalletContent
